import {Alert, Badge, Group, NumberInput, Paper, SegmentedControl, Stack, Switch, Text, TextInput, Title} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import Icon from "../../../components/Icon/Icon.tsx";
import IconMap from "../../../components/Icon/IconMap.ts";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api, {useEntityTypes} from "../../../utils/api.ts";
import {QueueEstimate, useProductionEstimate} from "../../../utils/production-estimate";
import {
  buildAccessibleMaterialTotals,
  buildMaterialsCatalogs,
  buildRequiredMaterialsForQueue,
  type MaterialsCatalogs,
  type MaterialTotals
} from "../../../utils/production-materials.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {EMPTY_RECORD, castString, formatDuration} from "./shared.ts";
import {hasDatacardForProduct} from "./plannerUtils.ts";
import {
  ProductionAction,
  ProductionMaterialsResponse,
  ProductionPlannedItem,
  ProductionQueueItem,
  ProductionTimelineResponse
} from "./types.ts";
import ProductionModuleTimelineMaterialsModal from "./ProductionModuleTimelineMaterialsModal.tsx";
import {GanttGroupSection, GanttRow, GanttSegment, MissingMaterialsQueueItemDetail} from "./ProductionModuleTimeline.types.ts";
import ProductionModuleTimelineView from "./ProductionModuleTimelineView.tsx";
import classes from "./ProductionModuleTimelinePage.module.css";
import {EntityType, getTypeFromUID} from "../../../utils/uid.ts";

function formatEta(value: number | null | undefined): string {
  if (!value || !Number.isFinite(value)) {
    return '-';
  }
  return DateTime.fromSeconds(Math.floor(value)).toFormat('LLL dd HH:mm');
}


function toActionRange(action: ProductionAction, nowSeconds: number): { start: number; end: number } | null {
  const end = action.endAt;
  const duration = action.delayRemaining ?? action.delayTotal;

  if (typeof end === 'number' && typeof action.delayRemaining === 'number' && action.delayRemaining > 0) {
    return {start: end - action.delayRemaining, end};
  }
  if (typeof end === 'number' && typeof action.delayTotal === 'number' && action.delayTotal > 0) {
    return {start: end - action.delayTotal, end};
  }
  if (typeof duration === 'number' && duration > 0) {
    return {start: nowSeconds, end: nowSeconds + duration};
  }
  if (typeof end === 'number') {
    return {start: Math.max(nowSeconds, end - 3600), end};
  }
  return null;
}

function queueDurationSeconds(item: ProductionQueueItem): number | null {
  if (typeof item.delayTotal === 'number' && item.delayTotal > 0) {
    return item.delayTotal;
  }
  if (typeof item.delayRemaining === 'number' && item.delayRemaining > 0) {
    return item.delayRemaining;
  }
  return null;
}

function normalizeManagementSkill(value: unknown): number | null {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.max(0, Math.min(5, parsed));
}

function formatDayRange(estimate: QueueEstimate | null): string {
  if (!estimate || estimate.status !== 'ok') {
    return '';
  }
  const min = estimate.daysMin;
  const max = estimate.daysMax;
  if (typeof min !== 'number' || typeof max !== 'number') {
    return '';
  }
  return `${min.toFixed(1)}-${max.toFixed(1)}d`;
}

function buildSegmentLabel(quantity: number, name: string): string {
  return `${quantity}x ${name || "Unknown"}`;
}

function toggleKey(current: string[], key: string): string[] {
  return current.includes(key)
    ? current.filter((currentKey) => currentKey !== key)
    : [...current, key];
}

type PendingSegment = Omit<GanttSegment, 'lane'>;
type MaterialDemandDetail = MissingMaterialsQueueItemDetail & {requiredMaterials: MaterialTotals};
const TIMELINE_LANE_COUNT = 3;
const RETOOL_VOLUME_BASE = 7750;

function retoolDays(volumeCap: number | null | undefined): number {
  const cap = Number(volumeCap || 0);
  if (!Number.isFinite(cap) || cap <= RETOOL_VOLUME_BASE) {
    return 0;
  }
  return Math.log(cap / RETOOL_VOLUME_BASE);
}

function getSegmentLayoutEnd(segment: PendingSegment): number {
  if (segment.end === null) {
    return segment.start + 1;
  }
  return Math.max(segment.start + 1, segment.end);
}

function assignSegmentLanes(segments: PendingSegment[]): GanttSegment[] {
  return [...segments]
    .sort((a, b) => {
      if (a.start !== b.start) {
        return a.start - b.start;
      }
      const aEnd = getSegmentLayoutEnd(a);
      const bEnd = getSegmentLayoutEnd(b);
      if (aEnd !== bEnd) {
        return aEnd - bEnd;
      }
      return a.key.localeCompare(b.key);
    })
    .map((segment, index) => {
      return {
        ...segment,
        lane: index % TIMELINE_LANE_COUNT,
      };
    });
}

function addMaterialTotals(target: MaterialTotals, source: MaterialTotals): void {
  Object.entries(source).forEach(([materialId, quantity]) => {
    target[materialId] = (target[materialId] ?? 0) + quantity;
  });
}

function buildRequiredMaterialsForPlannedProduct(
  productId: string,
  quantity: number,
  catalogs: MaterialsCatalogs
): MaterialTotals {
  const normalizedQuantity = Math.max(1, Number(quantity || 1));
  const productType = getTypeFromUID(productId);
  const entry = productType === EntityType.SHIPS
    ? catalogs.ships[productId]
    : productType === EntityType.VEHICLES
      ? catalogs.vehicles[productId]
      : productType === EntityType.ITEMS
        ? catalogs.items[productId]
        : productType === EntityType.DROIDS
          ? catalogs.droids[productId]
          : null;
  const totals: MaterialTotals = {};
  const materials = entry?.materials ?? null;

  if (!materials) {
    return totals;
  }

  Object.entries(materials).forEach(([materialId, value]) => {
    const base = Number(value || 0);
    if (base <= 0) {
      return;
    }
    totals[materialId] = base * normalizedQuantity;
  });

  return totals;
}

function ProductionModuleTimelinePage() {
  const {moduleId} = useParams<{ moduleId?: string }>();
  const moduleInfo = useProductionModule();
  const [windowMonthsValue, setWindowMonthsValue] = useState('12');
  const [showIdle, setShowIdle] = useState(false);
  const [managementSkill, setManagementSkill] = useState<number | ''>('');
  const [materialsDetailRow, setMaterialsDetailRow] = useState<GanttRow | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const query = useQuery<ProductionTimelineResponse>({
    queryKey: ['prod-module', moduleId, 'timeline'],
    queryFn: async () => (await api.get(`/modules/production/${moduleId}/timeline`)).data as ProductionTimelineResponse,
    enabled: !!moduleId,
  });
  const materialsQuery = useQuery<ProductionMaterialsResponse>({
    queryKey: ['prod-module', moduleId, 'materials'],
    queryFn: async () => (await api.get(`/modules/production/${moduleId}/materials`)).data as ProductionMaterialsResponse,
    enabled: !!moduleId,
  });
  const {stations, facilities, ships, vehicles, items, droids, materials} = useEntityTypes([
    'stations',
    'facilities',
    'ships',
    'vehicles',
    'items',
    'droids',
    'materials',
  ]);
  const {estimateQueuedItem} = useProductionEstimate();

  const now = useMemo(() => DateTime.now(), [query.dataUpdatedAt]);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const nowSeconds = Math.floor(now.toSeconds());
  const windowMonths = Math.max(1, Number.parseInt(windowMonthsValue, 10) || 1);
  const windowStartDate = now.startOf('day');
  const windowEndDate = windowStartDate.plus({months: windowMonths}).endOf('day');
  const windowStart = Math.floor(windowStartDate.toSeconds());
  const windowEnd = Math.floor(windowEndDate.toSeconds());
  const windowSeconds = Math.max(1, windowEnd - windowStart);
  const totalDays = Math.max(1, Math.ceil(windowSeconds / 86400));

  const labelColumnWidth = isMobile ? 220 : 320;
  const mobilePixelsPerDay = totalDays <= 45 ? 14 : totalDays <= 120 ? 9 : totalDays <= 240 ? 6 : 4;
  const mobileTimelineWidth = Math.max(480, Math.round(totalDays * mobilePixelsPerDay));
  const gridTemplateColumns = isMobile
    ? `${labelColumnWidth}px ${mobileTimelineWidth}px`
    : `${labelColumnWidth}px minmax(0, 1fr)`;
  const showDailyTicks = windowMonths === 1;

  const rows = useMemo(() => {
    if (!query.data) {
      return [];
    }

    const stationTypeNames = new Map(stations.map((type) => [type.id, type.name]));
    const facilityTypeNames = new Map(facilities.map((type) => [type.id, type.name]));
    const locationVolumeCap = new Map<string, number>();
    stations.forEach((type) => {
      const volumeCap = Number(type.volumecap ?? 0);
      if (volumeCap > 0) locationVolumeCap.set(type.id, volumeCap);
    });
    facilities.forEach((type) => {
      const volumeCap = Number(type.volumecap ?? 0);
      if (volumeCap > 0) locationVolumeCap.set(type.id, volumeCap);
    });
    const shipTypeNames = new Map(ships.map((type) => [type.id, type.name]));
    const vehicleTypeNames = new Map(vehicles.map((type) => [type.id, type.name]));
    const itemTypeNames = new Map(items.map((type) => [type.id, type.name]));
    const droidTypeNames = new Map(droids.map((type) => [type.id, type.name]));
    const materialTypeNames = new Map(materials.map((type) => [type.id, type.name]));
    const materialCatalogs = buildMaterialsCatalogs({ships, vehicles, items, droids});
    const moduleMaterials = materialsQuery.data?.materials ?? [];
    const materialsReady = !!materialsQuery.data && !materialsQuery.isError;
    const resolveTrackedEntityTypeName = (entityType: string, entityData: Record<string, unknown> = EMPTY_RECORD): string => {
      const typeId = castString(entityData, 'typeId');
      const fallbackTypeName = castString(entityData, 'typeName');
      if (entityType === 'station' && typeId !== '' && stationTypeNames.has(typeId)) {
        return stationTypeNames.get(typeId) || fallbackTypeName || 'Station';
      }
      if (entityType === 'facility' && typeId !== '' && facilityTypeNames.has(typeId)) {
        return facilityTypeNames.get(typeId) || fallbackTypeName || 'Facility';
      }
      if (fallbackTypeName !== '') {
        return fallbackTypeName;
      }
      return entityType === 'facility' ? 'Facility' : 'Station';
    };

    const resolveProducedName = (id: string): string => {
      const type = getTypeFromUID(id);
      const fallback = 'Unknown Product';
      if (id !== '' && type) {
        if (type === EntityType.SHIPS && shipTypeNames.has(id)) {
          return shipTypeNames.get(id) ?? fallback;
        }
        if (type == EntityType.VEHICLES && vehicleTypeNames.has(id)) {
          return vehicleTypeNames.get(id) ?? fallback;
        }
        if (type === EntityType.ITEMS && itemTypeNames.has(id)) {
          return itemTypeNames.get(id) ?? fallback;
        }
        if (type === EntityType.DROIDS && droidTypeNames.has(id)) {
          return droidTypeNames.get(id) ?? fallback;
        }
      }
      return fallback;
    };

    const mapped: GanttRow[] = query.data.timeline.map((row) => {
      const pendingSegments: PendingSegment[] = [];
      let cursorMin = nowSeconds;
      let cursorMax = nowSeconds;
      const rowEntityData = row.entityData ?? EMPTY_RECORD;
      const entityTypeName = resolveTrackedEntityTypeName(row.entityType, rowEntityData);
      const locationTypeId = castString(rowEntityData, 'typeId');
      const locationVolumeCapValue = locationVolumeCap.get(locationTypeId) ?? 0;
      const retoolDaysForLocation = retoolDays(locationVolumeCapValue);
      const retoolSeconds = Math.round(retoolDaysForLocation * 86400);
      let lastProductId: string | null = null;
      let activeEta = '-';
      let queueEta = '-';
      let queueHasUnknownEnd = false;
      let queueMinEndAt: number | null = null;
      let queueMaxEndAt: number | null = null;
      const materialDemandDetails: MaterialDemandDetail[] = [];

      interface RetoolInfo {
        seconds: number;
        segmentStart: number;
        retoolEnd: number;
        details: string[];
      }

      const maybeAddRetool = (nextProductId: string | null): RetoolInfo | null => {
        if (!nextProductId || retoolSeconds <= 0) {
          if (nextProductId) {
            lastProductId = nextProductId;
          }
          return null;
        }
        if (lastProductId === nextProductId) {
          return null;
        }
        const segmentStart = cursorMax;
        const retoolEnd = segmentStart + retoolSeconds;
        cursorMin += retoolSeconds;
        cursorMax += retoolSeconds;
        lastProductId = nextProductId;
        return {
          seconds: retoolSeconds,
          segmentStart,
          retoolEnd,
          details: [
            `Retool: ${formatDuration(retoolSeconds)}`,
          ],
        };
      };

      if (row.currentAction) {
        const range = toActionRange(row.currentAction, nowSeconds);
        if (range) {
          const entityName = row.currentAction?.productTypeName ?? (row.currentAction.productId ? resolveProducedName(row.currentAction.productId) : 'Unknown');
          const activePlan = row.currentAction.planMatch ?? null;
          lastProductId = row.currentAction.productId ?? null;
          pendingSegments.push({
            key: `${row.entityId}:current`,
            label: buildSegmentLabel(row.currentAction.quantity, entityName),
            start: range.start,
            end: range.end,
            kind: activePlan ? 'current_planned' : 'current',
            // Running actions already consumed their datacard use, so they're never flagged as missing.
            missingDatacard: false,
            details: [
              activePlan ? 'Current (planned)' : 'Current',
              `Name: ${row.currentAction.productName ?? ''}`,
              entityName,
              row.currentAction.productName ?? '',
              `Quantity: ${row.currentAction.quantity}`,
              `Status: ${row.currentAction.status || 'running'}`,
              `ETA: ${formatEta(range.end)}`,
              activePlan ? `Run: ${activePlan.runName}` : '',
            ].filter(Boolean),
          });
          cursorMin = range.end;
          cursorMax = range.end;
          activeEta = formatEta(range.end);
        }
      }

      let unknownOffset = 0;
      const queue = [...row.queuedItems].sort((a, b) => a.queueOrder - b.queueOrder);
      queue.forEach((item) => {
        const entityName = item.entityName || '';
        const productName = item.name || '';
        const planMatch = item.planMatch ?? null;

        const label = buildSegmentLabel(item.quantity, entityName);
        const queueMissingDatacard = !!item.entityId && !hasDatacardForProduct(row, item.entityId);
        const retool = maybeAddRetool(item.entityId || null);
        const estimateResult = estimateQueuedItem(
          {quantity: item.quantity, entityId: item.entityId, entityHref: item.entityHref},
          row.entityType,
          row.location as Record<string, string>,
          cursorMin,
          cursorMax,
          normalizeManagementSkill(managementSkill)
        );
        const estimate = estimateResult.estimate ?? null;

        if (estimate?.status === 'ok' && typeof estimate.endMinAt === 'number' && typeof estimate.endMaxAt === 'number') {
          const startMin = typeof estimate.startMinAt === 'number' ? estimate.startMinAt : cursorMin;
          const startMax = typeof estimate.startMaxAt === 'number' ? estimate.startMaxAt : cursorMax;
          const endMin = Math.max(startMin, estimate.endMinAt);
          const endMax = Math.max(endMin, estimate.endMaxAt);
          queueMinEndAt = queueMinEndAt === null ? endMin : Math.max(queueMinEndAt, endMin);
          queueMaxEndAt = queueMaxEndAt === null ? endMax : Math.max(queueMaxEndAt, endMax);
          pendingSegments.push({
            key: `${row.entityId}:queue:${item.queueOrder}:estimate`,
            label,
            start: retool ? retool.segmentStart : startMax,
            end: endMax,
            minEnd: endMin,
            retoolEnd: retool?.retoolEnd ?? null,
            kind: planMatch ? 'queue_planned_estimate' : 'queue_estimate',
            missingDatacard: queueMissingDatacard,
            details: [
              `Queued #${item.queueOrder}${planMatch ? ' (planned)' : ''}`,
              `Name: ${productName}`,
              entityName,
              `Quantity: ${item.quantity}`,
              `ETA: ${formatEta(endMin)} - ${formatEta(endMax)}`,
              `Estimate: ${formatDayRange(estimate)}`,
              ...(retool ? retool.details : []),
              planMatch ? `Run: ${planMatch.runName}` : '',
              queueMissingDatacard ? 'No datacard assigned for this product' : '',
            ].filter((line) => !!line && line !== 'Estimate: '),
          });
          materialDemandDetails.push({
            key: `${row.entityId}:materials:${item.queueOrder}`,
            queueOrder: item.queueOrder,
            title: `Queued #${item.queueOrder}`,
            itemLabel: label,
            requiredByAt: startMax,
            requiredByLabel: formatEta(startMax),
            requiredMaterials: buildRequiredMaterialsForQueue([item], materialCatalogs).materials,
            materials: [],
          });
          cursorMin = estimateResult.nextStartMinAt ?? endMin;
          cursorMax = estimateResult.nextStartMaxAt ?? endMax;
          return;
        }

        const duration = queueDurationSeconds(item);
        if (duration) {
          const start = cursorMax;
          const end = start + duration;
          queueMinEndAt = queueMinEndAt === null ? end : Math.max(queueMinEndAt, end);
          queueMaxEndAt = queueMaxEndAt === null ? end : Math.max(queueMaxEndAt, end);
          pendingSegments.push({
            key: `${row.entityId}:queue:${item.queueOrder}`,
            label,
            start: retool ? retool.segmentStart : start,
            end,
            retoolEnd: retool?.retoolEnd ?? null,
            kind: planMatch ? 'queue_planned' : 'queue',
            missingDatacard: queueMissingDatacard,
            details: [
              `Queued #${item.queueOrder}${planMatch ? ' (planned)' : ''}`,
              `Name: ${productName}`,
              entityName,
              `Quantity: ${item.quantity}`,
              `ETA: ${formatEta(end)}`,
              ...(retool ? retool.details : []),
              planMatch ? `Run: ${planMatch.runName}` : '',
              queueMissingDatacard ? 'No datacard assigned for this product' : '',
            ].filter(Boolean),
          });
          materialDemandDetails.push({
            key: `${row.entityId}:materials:${item.queueOrder}`,
            queueOrder: item.queueOrder,
            title: `Queued #${item.queueOrder}`,
            itemLabel: label,
            requiredByAt: start,
            requiredByLabel: formatEta(start),
            requiredMaterials: buildRequiredMaterialsForQueue([item], materialCatalogs).materials,
            materials: [],
          });
          cursorMin = end;
          cursorMax = end;
          return;
        }

        const markerAt = cursorMax + unknownOffset * 86400;
        queueHasUnknownEnd = true;
        pendingSegments.push({
          key: `${row.entityId}:queue:${item.queueOrder}:unknown`,
          label,
          start: markerAt,
          end: null,
          kind: planMatch ? 'queue_unknown_planned' : 'queue_unknown',
          missingDatacard: queueMissingDatacard,
          details: [
            `Queued #${item.queueOrder}${planMatch ? ' (planned)' : ''}`,
            `Name: ${productName}`,
            entityName,
            `Quantity: ${item.quantity}`,
            `Reason: ${estimate?.reason || 'duration unavailable'}`,
            ...(retool ? retool.details : []),
            planMatch ? `Run: ${planMatch.runName}` : '',
            queueMissingDatacard ? 'No datacard assigned for this product' : '',
          ].filter(Boolean),
        });
        materialDemandDetails.push({
          key: `${row.entityId}:materials:${item.queueOrder}`,
          queueOrder: item.queueOrder,
          title: `Queued #${item.queueOrder}`,
          itemLabel: label,
          requiredByAt: null,
          requiredByLabel: 'Unknown',
          requiredMaterials: buildRequiredMaterialsForQueue([item], materialCatalogs).materials,
          materials: [],
        });
        unknownOffset += 1;
      });

      if (queue.length > 0) {
        if (queueHasUnknownEnd || queueMinEndAt === null || queueMaxEndAt === null) {
          queueEta = 'Unknown';
        } else if (Math.abs(queueMaxEndAt - queueMinEndAt) >= 60) {
          queueEta = `${formatEta(queueMinEndAt)} - ${formatEta(queueMaxEndAt)}`;
        } else {
          queueEta = formatEta(queueMaxEndAt);
        }
      }

      let plannedMaterialOrder = 1;
      (row.plannedItems ?? []).forEach((plannedItem: ProductionPlannedItem) => {
        const quantity = Math.max(1, Number(plannedItem.quantity || 1));
        const itemName = resolveProducedName(plannedItem.productId);
        const runSkill = normalizeManagementSkill(plannedItem.managementSkill) ?? 0;
        const plannedLabel = buildSegmentLabel(quantity, itemName);
        const plannedMissingDatacard = !!plannedItem.productId && !hasDatacardForProduct(row, plannedItem.productId);
        const retool = maybeAddRetool(plannedItem.productId || null);
        const estimateResult = estimateQueuedItem(
          {quantity, entityId: plannedItem.productId},
          row.entityType,
          row.location as Record<string, string>,
          cursorMin,
          cursorMax,
          runSkill
        );
        const estimate = estimateResult.estimate ?? null;
        const planKey = `${plannedItem.runId}:${plannedItem.blockId || plannedItem.blockIndex}:${plannedItem.repeatIndex}`;

        if (estimate?.status === 'ok' && typeof estimate.endMinAt === 'number' && typeof estimate.endMaxAt === 'number') {
          const startMin = typeof estimate.startMinAt === 'number' ? estimate.startMinAt : cursorMin;
          const startMax = typeof estimate.startMaxAt === 'number' ? estimate.startMaxAt : cursorMax;
          const endMin = Math.max(startMin, estimate.endMinAt);
          const endMax = Math.max(endMin, estimate.endMaxAt);
          pendingSegments.push({
            key: `${row.entityId}:plan:${planKey}`,
            label: plannedLabel,
            start: retool ? retool.segmentStart : startMax,
            end: endMax,
            minEnd: endMin,
            retoolEnd: retool?.retoolEnd ?? null,
            kind: 'planned',
            missingDatacard: plannedMissingDatacard,
            details: [
              'Planned',
              `Run: ${plannedItem.runName}`,
              itemName,
              `Quantity: ${quantity}`,
              `ETA: ${formatEta(endMin)} - ${formatEta(endMax)}`,
              `Estimate: ${formatDayRange(estimate)}`,
              ...(retool ? retool.details : []),
              plannedMissingDatacard ? 'No datacard assigned for this product' : '',
            ].filter((line) => !!line && line !== 'Estimate: '),
          });
          materialDemandDetails.push({
            key: `${row.entityId}:materials:planned:${planKey}`,
            queueOrder: queue.length + plannedMaterialOrder,
            title: `Planned: ${plannedItem.runName}`,
            itemLabel: plannedLabel,
            requiredByAt: startMax,
            requiredByLabel: formatEta(startMax),
            requiredMaterials: buildRequiredMaterialsForPlannedProduct(plannedItem.productId, quantity, materialCatalogs),
            materials: [],
          });
          plannedMaterialOrder += 1;
          cursorMin = estimateResult.nextStartMinAt ?? endMin;
          cursorMax = estimateResult.nextStartMaxAt ?? endMax;
          return;
        }

        const markerAt = cursorMax + unknownOffset * 86400;
        pendingSegments.push({
          key: `${row.entityId}:plan:${planKey}:unknown`,
          label: plannedLabel,
          start: markerAt,
          end: null,
          kind: 'queue_unknown',
          missingDatacard: plannedMissingDatacard,
          details: [
            'Planned',
              `Run: ${plannedItem.runName}`,
              itemName,
              `Quantity: ${quantity}`,
              `Reason: ${estimate?.reason || 'duration unavailable'}`,
              ...(retool ? retool.details : []),
              plannedMissingDatacard ? 'No datacard assigned for this product' : '',
          ].filter(Boolean),
        });
        materialDemandDetails.push({
          key: `${row.entityId}:materials:planned:${planKey}:unknown`,
          queueOrder: queue.length + plannedMaterialOrder,
          title: `Planned: ${plannedItem.runName}`,
          itemLabel: plannedLabel,
          requiredByAt: null,
          requiredByLabel: 'Unknown',
          requiredMaterials: buildRequiredMaterialsForPlannedProduct(plannedItem.productId, quantity, materialCatalogs),
          materials: [],
        });
        plannedMaterialOrder += 1;
        unknownOffset += 1;
      });

      const segments = assignSegmentLanes(pendingSegments);
      const requiredMaterials: MaterialTotals = {};
      materialDemandDetails.forEach((detail) => addMaterialTotals(requiredMaterials, detail.requiredMaterials));
      const hasRequired = Object.keys(requiredMaterials).length > 0;
      let materialsStatus: GanttRow['materialsStatus'] = {
        status: 'none',
        label: 'No materials needed',
        color: 'gray',
        tooltip: 'No materials needed',
        details: [],
      };

      if (hasRequired) {
        if (!materialsReady) {
          materialsStatus = {
            status: 'unknown',
            label: 'Materials: unknown',
            color: 'yellow',
            tooltip: 'Material inventory not loaded yet.',
            details: [],
          };
        } else {
          const available = buildAccessibleMaterialTotals(
            {
              entityId: row.entityId,
              entityType: row.entityType,
              location: row.location,
            },
            moduleMaterials
          );
          const missing: Array<{ name: string; missing: number }> = [];
          const remainingAvailable = {...available};

          materialDemandDetails.forEach((detail) => {
            if (Object.keys(detail.requiredMaterials).length === 0) {
              return;
            }

            Object.entries(detail.requiredMaterials).forEach(([materialId, requiredQty]) => {
              const availableQty = remainingAvailable[materialId] ?? 0;
              const missingQty = Math.max(0, requiredQty - availableQty);
              if (missingQty > 0) {
                detail.materials.push({
                  name: materialTypeNames.get(materialId) ?? materialId,
                  available: availableQty,
                  required: requiredQty,
                  missing: missingQty,
                });
              }
              remainingAvailable[materialId] = Math.max(0, availableQty - requiredQty);
            });
          });

          Object.entries(requiredMaterials).forEach(([materialId, requiredQty]) => {
            const availableQty = available[materialId] ?? 0;
            const missingQty = Math.max(0, requiredQty - availableQty);
            if (missingQty > 0) {
              missing.push({
                name: materialTypeNames.get(materialId) ?? materialId,
                missing: missingQty,
              });
            }
          });

          if (missing.length > 0) {
            const topMissing = missing
              .sort((a, b) => b.missing - a.missing)
              .slice(0, 3)
              .map((entry) => `${entry.name}: ${Math.ceil(entry.missing)}`);
            const extra = missing.length > 3 ? ` +${missing.length - 3} more` : '';
            materialsStatus = {
              status: 'missing',
              label: `Materials: missing ${missing.length}`,
              color: 'red',
              tooltip: `Missing ${missing.length} material types. ${topMissing.join(' | ')}${extra}.`,
              details: materialDemandDetails
                .filter((detail) => detail.materials.length > 0)
                .map(({requiredMaterials: _requiredMaterials, ...detail}) => detail),
            };
          } else {
            materialsStatus = {
              status: 'ok',
              label: 'Materials: ok',
              color: 'green',
              tooltip: 'All required materials are available.',
              details: [],
            };
          }
        }
      }

      return {
        entityId: row.entityId,
        name: row.name,
        entityType: row.entityType,
        entityTypeName,
        groups: row.groups ?? [],
        location: row.location,
        syncStatus: row.syncStatus,
        queuedItems: row.queuedItems.length,
        activeEta,
        queueEta,
        segments,
        materialsStatus,
      };
    });

    return mapped.sort((a, b) => {
      const aCurrent = a.segments.find((segment) => segment.kind === 'current')?.end ?? Number.MAX_SAFE_INTEGER;
      const bCurrent = b.segments.find((segment) => segment.kind === 'current')?.end ?? Number.MAX_SAFE_INTEGER;
      if (aCurrent !== bCurrent) {
        return aCurrent - bCurrent;
      }

      const aFirst = a.segments.reduce((min, segment) => Math.min(min, segment.start), Number.MAX_SAFE_INTEGER);
      const bFirst = b.segments.reduce((min, segment) => Math.min(min, segment.start), Number.MAX_SAFE_INTEGER);
      if (aFirst !== bFirst) {
        return aFirst - bFirst;
      }

      return a.name.localeCompare(b.name);
    });
  }, [
    query.data,
    nowSeconds,
    estimateQueuedItem,
    managementSkill,
    stations,
    facilities,
    ships,
    vehicles,
    items,
    droids,
    materials,
    materialsQuery.data,
    materialsQuery.isError,
  ]);

  const isVisibleInWindow = (segment: GanttRow["segments"][number]): boolean => {
    if (segment.end === null) {
      return segment.start >= windowStart && segment.start <= windowEnd;
    }
    return segment.end > windowStart && segment.start < windowEnd;
  };

  const visibleRows = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredRows = showIdle
      ? rows
      : rows.filter((row) => row.segments.some(isVisibleInWindow));

    if (normalizedSearch === '') {
      return filteredRows;
    }

    return filteredRows.filter((row) => row.name.toLowerCase().includes(normalizedSearch));
  }, [rows, showIdle, searchValue, windowStart, windowEnd]);

  const groupedVisibleRows = useMemo<GanttGroupSection[]>(() => {
    const responseGroups = query.data?.groups ?? [];
    const sections: GanttGroupSection[] = [];

    responseGroups.forEach((group) => {
      const groupRows = visibleRows.filter((row) => (row.groups ?? []).some((groupRef) => groupRef.id === group.id));
      if (groupRows.length > 0) {
        sections.push({
          key: group.id,
          name: group.name,
          rows: groupRows,
        });
      }
    });

    const ungroupedRows = visibleRows.filter((row) => (row.groups ?? []).length === 0);
    if (ungroupedRows.length > 0) {
      sections.push({
        key: 'ungrouped',
        name: 'Ungrouped',
        rows: ungroupedRows,
      });
    }

    return sections;
  }, [visibleRows, query.data?.groups]);

  const collapsedGroupSet = useMemo(() => new Set(collapsedGroups), [collapsedGroups]);

  const monthBands = useMemo(() => {
    const bands: Array<{ key: string; left: number; width: number; label: string, mobileLabel: string }> = [];
    let cursor = DateTime.fromSeconds(windowStart).startOf('month');
    const end = DateTime.fromSeconds(windowEnd).startOf('month').plus({months: 1});

    while (cursor < end) {
      const nextMonth = cursor.plus({months: 1});
      const monthStart = Math.max(windowStart, Math.floor(cursor.toSeconds()));
      const monthEnd = Math.min(windowEnd, Math.floor(nextMonth.toSeconds()));
      if (monthEnd > monthStart) {
        const left = ((monthStart - windowStart) / windowSeconds) * 100;
        const width = ((monthEnd - monthStart) / windowSeconds) * 100;
        bands.push({
          key: cursor.toFormat('yyyy-LL'),
          left,
          width,
          label: cursor.toFormat('LLLL'),
          mobileLabel: cursor.toFormat('LLL'),
        });
      }
      cursor = nextMonth;
    }

    return bands;
  }, [windowStart, windowEnd, windowSeconds]);

  const monthDividers = useMemo(
    () => monthBands.map((band) => ({key: band.key, left: band.left})),
    [monthBands]
  );

  const gridTicks = useMemo(() => {
    if (!showDailyTicks) {
      return [];
    }
    const ticks: Array<{ key: string; left: number }> = [];
    let cursor = DateTime.fromSeconds(windowStart).startOf('day');
    const end = DateTime.fromSeconds(windowEnd).startOf('day');

    while (cursor <= end) {
      const ts = Math.floor(cursor.toSeconds());
      ticks.push({
        key: cursor.toISODate() ?? String(ts),
        left: ((ts - windowStart) / windowSeconds) * 100,
      });
      cursor = cursor.plus({days: 1});
    }

    return ticks;
  }, [showDailyTicks, windowStart, windowEnd, windowSeconds]);

  const weekDividers = useMemo(() => {
    const ticks: Array<{ key: string; left: number }> = [];
    let cursor = DateTime.fromSeconds(windowStart).startOf('week');
    const end = DateTime.fromSeconds(windowEnd).endOf('week');

    while (cursor <= end) {
      const ts = Math.floor(cursor.toSeconds());
      if (ts >= windowStart && ts <= windowEnd) {
        ticks.push({
          key: `week-${cursor.toISODate() ?? ts}`,
          left: ((ts - windowStart) / windowSeconds) * 100,
        });
      }
      cursor = cursor.plus({weeks: 1});
    }

    return ticks;
  }, [windowStart, windowEnd, windowSeconds]);

  if (!moduleId) {
    return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
  }
  if (query.isPending) {
    return <LoaderScreen message="Loading timeline"/>;
  }
  if (query.isError) {
    return <Page><Alert color="red" title="Timeline unavailable">Could not load timeline data.</Alert></Page>;
  }

  return (
    <Page>
      <title>{moduleInfo?.label || 'Production'} - Timeline</title>
      <Stack gap="md">
        <Title order={1}>{moduleInfo?.label || 'Production'} - Timeline</Title>
        <ProductionModuleNav moduleId={moduleId}/>

        <Paper withBorder p="md">
          <Group justify="space-between" align="end">
            <Stack gap={2}>
              <Text fw={700}>Gantt View</Text>
              <Text size="sm" c="dimmed">
                Window: {DateTime.fromSeconds(windowStart).toFormat('LLL dd, yyyy')} to {DateTime.fromSeconds(windowEnd).toFormat('LLL dd, yyyy')}
              </Text>
            </Stack>
            <Group gap="md" align="end">
              <TextInput
                label="Search location"
                value={searchValue}
                onChange={(event) => setSearchValue(event.currentTarget.value)}
                placeholder="Search by name"
                w={220}
              />
              <NumberInput
                label="Management"
                value={managementSkill}
                onChange={(value) => {
                  const normalized = normalizeManagementSkill(value);
                  setManagementSkill(normalized === null ? '' : normalized);
                }}
                min={0}
                max={5}
                clampBehavior="strict"
                step={1}
                allowDecimal={false}
                placeholder="Range"
                w={110}
              />
              <SegmentedControl
                value={windowMonthsValue}
                onChange={setWindowMonthsValue}
                data={[
                  {value: '1', label: '1m'},
                  {value: '3', label: '3m'},
                  {value: '6', label: '6m'},
                  {value: '12', label: '12m'},
                ]}
              />
              <Switch
                label="Show idle"
                checked={showIdle}
                onChange={(event) => setShowIdle(event.currentTarget.checked)}
              />
            </Group>
          </Group>

          <Group gap="xs" mt="sm">
            <Badge color="green" variant="filled">Current</Badge>
            <Badge color="teal" variant="filled">Current (planned)</Badge>
            {managementSkill !== '' ?
              <>
                <Badge color="red" variant="filled">Queued (unplanned)</Badge>
                <Badge color="blue" variant="filled">Queued (planned)</Badge>
              </>
              :
              <>
                <Badge color="yellow" variant="filled">Queued (unplanned estimate)</Badge>
                <Badge color="cyan" variant="filled">Queued (planned estimate)</Badge>
              </>
            }
            <Badge color="grape" variant="filled">Planned runs</Badge>
            <Badge
              color="red"
              variant="outline"
              leftSection={<Icon icon={IconMap.Datacard} size={11} color="var(--mantine-color-red-6)"/>}
            >
              Missing datacard
            </Badge>
          </Group>
        </Paper>

        {rows.length === 0 ? <Paper withBorder p="md"><Text>No tracked entities yet.</Text></Paper> : null}
        {rows.length > 0 && visibleRows.length === 0 ? (
          <Paper withBorder p="md"><Text>No production items fall in the selected time window.</Text></Paper>
        ) : null}

        {visibleRows.length > 0 ? (
          <Paper withBorder p="sm" className={classes.timelinePaper}>
            <ProductionModuleTimelineView
              groupedVisibleRows={groupedVisibleRows}
              collapsedGroupSet={collapsedGroupSet}
              isMobile={isMobile}
              monthBands={monthBands}
              monthDividers={monthDividers}
              gridTicks={gridTicks}
              weekDividers={weekDividers}
              windowStart={windowStart}
              windowEnd={windowEnd}
              windowSeconds={windowSeconds}
              gridTemplateColumns={gridTemplateColumns}
              labelColumnWidth={labelColumnWidth}
              mobileTimelineWidth={mobileTimelineWidth}
              onToggleGroup={(groupKey) => setCollapsedGroups((current) => toggleKey(current, groupKey))}
              onOpenMaterialsDetail={setMaterialsDetailRow}
            />
          </Paper>
        ) : null}
      </Stack>
      <ProductionModuleTimelineMaterialsModal
        row={materialsDetailRow}
        onClose={() => setMaterialsDetailRow(null)}
      />
    </Page>
  );
}

export default withRouteErrorBoundary(ProductionModuleTimelinePage);
